/**
 * @Description 获取用户当前位置
 * 通过高德地图获取当前用户所在的位置信息
 */

import { useEffect, useState } from 'react';
import { isArray, isNotEmptyAny } from '@lhb/func';
import { fetchCityIdByName } from '@/common/api/selection';
import { getCurPosition, getLngLatAddress } from '@/common/utils/map';

/**
 * 获取用户定位信息，默认返回省市区的id和name
 * @param mapIns 地图实例
 * @param config 自定义配置
 * @param hasAddress 是否需要定位的地址
 */
export function useCurLocation(
  mapIns,
  config = {},
  hasAddress = false, // 是否需要返回具体的位置信息
) {
  const [locationInfo, setLocationInfo] = useState<any>({}); // 用户当前位置信息
  useEffect(() => {
    if (!mapIns) return;
    getLocation();
  }, [mapIns]);

  const getLocation = async () => {
    const res: any = {};
    try {
      const info: any = await getCurPosition(mapIns, {
        // https://lbs.amap.com/api/javascript-api-v2/documentation#geolocation
        // 是否显示定位按钮
        showButton: false,
        showCircle: true,
        showMarker: false,
        ...config,
      });
      /*
        拿到用户所在的省市信息和定位
        拿到的数据有两种格式，需要兼容

        "location_type"为"html5"时：
        {"status":0,"code":0,"info":"SUCCESS","position":[120.045181,30.291026],"location_type":"html5","message":"Get ipLocation failed.Get geolocation success.Convert Success.Get address success.","accuracy":35,"isConverted":false,"altitude":9.306496620178223,"altitudeAccuracy":13.124226570129395,"heading":null,"speed":null,"addressComponent":{"citycode":"0571","adcode":"330110","businessAreas":[{"name":"西溪","id":"330106","location":[120.09103,30.271791]}],"neighborhoodType":"","neighborhood":"","building":"","buildingType":"","street":"同顺街","streetNumber":"74号","province":"浙江省","city":"杭州市","district":"余杭区","township":"五常街道"},"formattedAddress":"浙江省杭州市余杭区五常街道景钊棋牌茶馆(八方城店)西溪八方城","roads":[],"crosses":[],"pois":[]}

        "location_type"为"ipcity"时：
        {"country":"中华人民共和国","province":"浙江省","city":"杭州市","citycode":"0571","adcode":"330100","bounds":[118.344933,29.188757,120.721945,30.566516],"status":0,"position":[120.153576,30.287459],"location_type":"ipcity","isConverted":true,"message":"Get ipLocation failed.Get geolocation timeout.Get city by ip success.No need convert.city center no address."}
      */
      let city;
      let province;
      let position;
      const { addressComponent } = info || {};
      if (isNotEmptyAny(addressComponent)) {
        const { city: innerCity, province: innerProvince } = addressComponent;
        position = [info.position?.lng, info.position?.lat];
        city = innerCity;
        province = innerProvince;
      } else {
        city = info?.city;
        province = info?.province;
        position = info?.position;
      }
      const name = city || province; // 直辖市没有city名
      if (isArray(position) && position.length) { // 需要地址信息
        if (hasAddress) {
          const address = await getLngLatAddress(position);
          address && (res.address = address);
        }
        +(position[0]) && (res.lng = +position[0]);
        +(position[1]) && (res.lat = +position[1]);
      }
      // 拿到区的信息（是因为getCurPosition中的zoomToAccuracy这个参数）
      mapIns.getCity(async (curInfo: any) => {
        const { district } = curInfo; // 拿到当前所在的区
        if (!name) return;
        const cityInfo = await fetchCityIdByName({ name }); // 通过城市查找该城市下所有的区
        const { districtList, id: cityId, provinceId, name: cityName } = cityInfo || {};
        res.provinceId = provinceId;
        res.provinceName = province;
        res.cityId = cityId;
        res.cityName = cityName;
        // 匹配到对应的区信息
        const targetDistrict = districtList?.find((districtItem: any) => districtItem?.name?.includes(district));
        if (targetDistrict) {
          const { id: districtId, name: districtName } = targetDistrict;
          res.districtId = districtId;
          res.districtName = districtName;
          setLocationInfo(res);
          return;
        }
        setLocationInfo(res);
      });
    } catch (error) {
      console.log(`获取用户定位失败：`, error);
      const { defaultWhenFails } = config as any;
      if (defaultWhenFails) { // 定位失败时返回默认值
        setLocationInfo({
          provinceId: '浙江省',
          provinceName: 11,
          cityId: 87,
          cityName: '杭州市',
          lng: 120.210792,
          lat: 30.246026,
          isDefaultWhenFails: true, // 定位失败时的默认值
        });
      }
    }
  };

  return locationInfo;
}
